import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { notify } from '../../services/notification';
import { Upload, X } from "lucide-react";

import "./pincodeMasterList.scss";
import PincodeTable from "./PincodeTable";
import PincodeUploadModal from "./PincodeUploadModal";
import { constant } from "../../constants/constant";
import Header from "../../component/loaderComponent/Header/Header";
import { getPincodeMasterListAction, downloadPincodeMasterExcelAction } from "../../actions/pincodeAction";
import { Image_Url } from "../../constants/imageUrl/ImageUrl";

export default function PincodeMasterList() {
  const dispatch = useDispatch();
  const pageSize = constant?.paginationCount || 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchField, setSearchField] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [pincodeList, setPincodeList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);

  const searchFieldOptions = [
    { value: "pincode", label: "Pincode" },
    { value: "state_name", label: "State Name" },
    { value: "city_name", label: "City Name" },
    { value: "district_name", label: "District Name" },
    { value: "aps_code", label: "APS Code" },
    { value: "country", label: "Country" },
    // { value: "aps_name", label: "APS Name" },
    // { value: "area_head_email", label: "Area Head Email" },
    // { value: "aps_mobile_number", label: "APS Mobile Number" },
    // { value: "zone", label: "Zone" }
  ];

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((totalCount || 0) / pageSize)),
    [totalCount, pageSize]
  );

  const pageWindow = useMemo(() => {
    const pages = [];
    const maxButtons = 7;

    if (totalPages <= maxButtons) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }

    const showLeftEllipsis = currentPage > 4;
    const showRightEllipsis = currentPage < totalPages - 3;

    pages.push(1);
    if (showLeftEllipsis) pages.push("left-ellipsis");

    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);

    for (let i = start; i <= end; i++) pages.push(i);

    if (showRightEllipsis) pages.push("right-ellipsis");
    pages.push(totalPages);

    return pages;
  }, [currentPage, totalPages]);

  const fetchPincodeList = async (
    page = currentPage,
    field = searchField,
    value = searchValue
  ) => {
    try {
      setLoading(true);
      let queryParam = "";

      if (field && value.trim()) {
        queryParam += `&${field}=${value.trim()}`;
      }

      const res = await dispatch(
        getPincodeMasterListAction(page, pageSize, queryParam)
      );

      if (res?.status === 200) {
        const apiData = res?.data?.results?.data || [];
        setPincodeList(Array.isArray(apiData) ? apiData : []);
        const count = res?.data?.count || 0;
        setTotalCount(count);

        const lastPage = Math.max(1, Math.ceil(count / pageSize));
        if (page > lastPage) setCurrentPage(lastPage);
      }
    } catch (error) {
      notify.error("Failed to load Pincode Master List");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    // use the values from this render
    fetchPincodeList(currentPage, searchField, searchValue);
  }, [currentPage]);  // keep deps as you had

  const handleSearch = () => {
    if (!searchField || !searchValue.trim()) return;

    const trimmed = searchValue.trim();

    if (currentPage !== 1) {
      // changing page will trigger useEffect and call fetchPincodeList with latest state
      setCurrentPage(1);
    } else {
      // already on page 1, so manually call with explicit params
      fetchPincodeList(1, searchField, trimmed);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && searchField && searchValue.trim()) {
      handleSearch();
    }
  };

  const handleClearSearch = () => {
    setSearchField("");
    setSearchValue("");

    if (currentPage !== 1) {
      // useEffect will run with currentPage = 1 and field/value = ""
      setCurrentPage(1);
    } else {
      // already on page 1, call explicitly with empty filters
      fetchPincodeList(1, "", "");
    }
  };

  const handleDownloadExcel = async () => {
    try {
      setDownloading(true);
      let queryParam = "";

      // Use same logic as fetchPincodeList
      if (searchField && searchValue.trim()) {
        queryParam = `${searchField}=${searchValue.trim()}`;
      }

      const fileBlob = await dispatch(downloadPincodeMasterExcelAction(queryParam));

      if (fileBlob) {
        const blobUrl = URL.createObjectURL(fileBlob);
        const newWindow = window.open(blobUrl, '_blank');
        if (newWindow) {
          newWindow.focus();
        } else {
          notify.error('Popup blocked. Please allow popups for this website');
        }
      }
    } catch (error) {
      notify.error('Error downloading file');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="pincode-wrapper">
      <header className="pincode-header">
        <Header title={"Pincode Master List"} />
        <p className="pincode-subtitle">
          View and download pincode master data with state, city, district, and APS code information
        </p>
      </header>

      <section className="pincode-search-card">
        <div className="pincode-searchbar">
          <div className="pincode-search-controls">
            {/* Dropdown */}
            <select
              className="pincode-dropdown"
              value={searchField}
              onChange={(e) => {
                setSearchField(e.target.value);
                setSearchValue("");
              }}
            >
              <option value="">Select Field</option>
              {searchFieldOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            {/* Input field */}
            <div className="pincode-input-wrapper">
              <span className="icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="7" />
                  <path d="M21 21l-4.35-4.35" />
                </svg>
              </span>
              <input
                type="text"
                placeholder={searchField ? `Enter ${searchFieldOptions.find(opt => opt.value === searchField)?.label}` : "Select a field first"}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={!searchField}
              />
              {/* Clear icon on right - conditionally rendered */}
              {(searchField || searchValue) && (
                <button
                  className="pincode-clear-icon"
                  onClick={handleClearSearch}
                  disabled={loading}
                  aria-label="Clear search"
                >
                  <X size={16} />
                </button>
              )}
            </div>

            {/* Search Button */}
            <button
              className="pincode-search-btn"
              onClick={handleSearch}
              disabled={!searchField || !searchValue.trim() || loading}
            >
              {/* <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
              </svg> */}
              Search
            </button>
          </div>

          {/* Create Button */}
          <button
            className="pincode-create-btn"
            onClick={() => setShowUploadModal(true)}
          >
            <Upload size={18} />
            Create
          </button>

          {/* Download Button */}
          <button
            className="pincode-download-btn"
            onClick={handleDownloadExcel}
            disabled={downloading || loading}
          >
            <img src={Image_Url.EXCEL_LOGO} className="excel-icon" alt="Excel" />
            {downloading ? 'Downloading...' : 'Download Excel'}
          </button>
        </div>
      </section>

      <PincodeTable
        data={pincodeList}
        totalPages={totalPages}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        loading={loading}
        totalCount={totalCount}
        pageSize={pageSize}
        pageWindow={pageWindow}
      />

      {/* Upload Modal */}
      <PincodeUploadModal
        open={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUploadSuccess={fetchPincodeList}
      />
    </div>
  );
}
